import React, { useEffect, useRef, useState } from "react";
import { toast } from "../../../../components/common/Toast/toast";
import { getTrainerDetail } from "../../../../../services/ClassroomManagementService.js";
import useDebouncedCallback from "../../../../components/common/useDebouncedCallback";
import { minutesToTimeLabel } from "../../classroomSchedule.js";
import PopupDone, { usePopupDone } from "../../../../components/common/PopupDone/PopupDone.jsx";

const DASH = "—";

/* How each batch status is tagged. The status itself is decided by the API
   (trainerDetail), against the same "running today" window the list uses. */
const BATCH_STATUS = {
  running: { label: "Running", className: "pgm-pill pgm-pill--good" },
  upcoming: { label: "Upcoming", className: "pgm-pill" },
  unscheduled: { label: "No dates", className: "pgm-pill" },
  completed: { label: "Completed", className: "pgm-pill" },
};

const formatDay = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    : DASH;

/**
 * One trainer in full -- the Trainers tab's view popup.
 *
 * Built to view_popup_rules in instruction_ui.json, which is to say exactly
 * like the /list-attendance record popup: the same frame, head, sections,
 * table and footer, and no class that popup does not already use (the one
 * addition is .pgm-row--open, the cursor and hover a row needs once it
 * opens something).
 *
 * `detail` is what GET /classroom-management/trainer-detail/:id returned;
 * the tab fetches it before opening. The batch search asks the API again
 * rather than filtering the rows already here.
 */
const TrainerRecordModal = ({ detail, canEdit, canDelete, onClose, onEdit, onDelete, onOpenBatch }) => {
  const [query, setQuery] = useState("");
  const [batches, setBatches] = useState(detail?.batches || []);
  const [matched, setMatched] = useState(detail?.matched ?? (detail?.batches || []).length);
  // Which search the answer belongs to: a slow reply must not land on top
  // of a query typed after it.
  const seq = useRef(0);
  // Mounted means open: a delete confirmed from here shows the done panel
  // in place of the record, and the popup closes itself after five seconds.
  // A form opened over this one registers after it, so its save is its own.
  const { done } = usePopupDone(true);

  useEffect(() => {
    const onKey = (e) => {
      // A form opened from here sits on top; Escape is its, not this popup's.
      if (e.key === "Escape" && !document.querySelector(".pg-modal--over")) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // The query and the list are reset per record, not per render.
  const trainerId = detail?.trainer?.id;
  useEffect(() => {
    seq.current += 1;
    setQuery("");
    setBatches(detail?.batches || []);
    setMatched(detail?.matched ?? (detail?.batches || []).length);
  }, [trainerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const runSearch = useDebouncedCallback(async (value) => {
    const mine = ++seq.current;
    try {
      const result = await getTrainerDetail(trainerId, { search: value.trim() });
      if (mine !== seq.current || !result) return;
      setBatches(result.batches || []);
      setMatched(result.matched ?? 0);
    } catch (error) {
      if (mine === seq.current) toast.error("Could not search this trainer's batches");
    }
  });

  if (!detail?.trainer) return null;

  const { trainer, counts = {}, today = null } = detail;
  const busy = trainer.availability === "busy";
  const onLeave = Boolean(today?.onLeave);
  const timeOf = (w) => `${minutesToTimeLabel(w.startMin)} – ${minutesToTimeLabel(w.endMin)}`;
  const name = trainer.trainerName || trainer.trainerEmail || "Unnamed trainer";
  const openBatch = (id) => {
    if (id) onOpenBatch(id);
  };

  return (
    <div
      className="pg-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pgm-trainer-title"
      onClick={onClose}
    >
      <div
        className="pg-modal-panel pg-modal-panel--wide"
        onClick={(e) => e.stopPropagation()}
      >
        {/* The trainer is what the record is about, so they are the title --
            in full, never clipped the way the list column clips a name. */}
        <header className="pgm-head">
          <div className="pgm-head-main">
            <h2 className="pgm-batch" id="pgm-trainer-title">
              {name}
            </h2>
            <dl className="pgm-ident">
              <div className="pgm-ident-row">
                <dt>Email</dt>
                <dd>{trainer.trainerEmail || DASH}</dd>
              </div>
              <div className="pgm-ident-row">
                <dt>Specialization</dt>
                <dd>{trainer.specialization || DASH}</dd>
              </div>
              <div className="pgm-ident-row">
                <dt>Experience</dt>
                <dd>{trainer.experience || DASH}</dd>
              </div>
              <div className="pgm-ident-row">
                <dt>Today</dt>
                <dd>
                  {onLeave ? "On leave" : busy ? "Busy" : "Free"}
                  {!onLeave && busy && trainer.busyUntil ? ` until ${formatDay(trainer.busyUntil)}` : ""}
                </dd>
              </div>
            </dl>
          </div>

          <button
            type="button"
            className="pg-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </header>

        <div className="pg-modal-body">
          {done ? (
            <PopupDone message={done} onClose={onClose} />
          ) : (
            <>
              {/* The figures first: how much this trainer is carrying, before the
                  list of what it is. */}
              <section className="pgm-section">
                <h3 className="pgm-section-title">Workload</h3>
                <div className="pgm-tiles">
                  <div className="pgm-tile">
                    <b>{counts.running ?? 0}</b>
                    <span>Running</span>
                  </div>
                  <div className="pgm-tile">
                    <b>{counts.upcoming ?? 0}</b>
                    <span>Upcoming</span>
                  </div>
                  <div className="pgm-tile">
                    <b>{counts.completed ?? 0}</b>
                    <span>Completed</span>
                  </div>
                  <div className="pgm-tile">
                    <b>{counts.total ?? 0}</b>
                    <span>Total batches</span>
                  </div>
                </div>
              </section>

              {/* Today, to the minute: when this trainer is teaching and when
                  they are not. Worked out by the API (trainerDetail -> today)
                  with the same session and free-window rules as the dashboard's
                  day popups, so the two never disagree about a trainer's day. */}
              {today && (
                <section className="pgm-section">
                  <h3 className="pgm-section-title">
                    Today
                    <span className="pgm-section-count">{(today.busy || []).length}</span>
                  </h3>

                  {onLeave ? (
                    <p className="pgm-empty">
                      On leave today &mdash; {today.onLeave.reason || "no reason given"}
                      {today.onLeave.note ? ` (${today.onLeave.note})` : ""}
                    </p>
                  ) : (
                    <>
                      {(today.busy || []).length === 0 ? (
                        <p className="pgm-empty">No sessions today.</p>
                      ) : (
                        <table className="pgm-table">
                          <thead>
                            <tr>
                              <th className="pgm-num">#</th>
                              <th>Busy</th>
                              <th>Batch</th>
                              <th>Room</th>
                            </tr>
                          </thead>
                          <tbody>
                            {today.busy.map((s, i) => (
                              <tr
                                key={`${s.batchId}-${s.startMin}`}
                                className="pgm-row--open"
                                tabIndex={0}
                                role="button"
                                aria-label={`Open batch ${s.batchName || ""}`.trim()}
                                onClick={() => openBatch(s.batchId)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    openBatch(s.batchId);
                                  }
                                }}
                              >
                                <td className="pgm-num">{i + 1}</td>
                                <td className="pgm-tel">{timeOf(s)}</td>
                                <td>
                                  <span className="pgm-person-id">
                                    <b className="pgm-person-name">{s.batchName || DASH}</b>
                                    <span className="pgm-person-mail">{s.courseName || "No course"}</span>
                                  </span>
                                </td>
                                <td>
                                  {s.classroomNo || DASH}
                                  {s.shifted ? " (shifted)" : ""}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}

                      {(today.free || []).length > 0 && (
                        <>
                          <h4 className="pgm-section-title">Free</h4>
                          <div className="cls-legend">
                            {today.free.map((w) => (
                              <span key={`${w.startMin}-${w.endMin}`} className="pg-pill">
                                {timeOf(w)}
                              </span>
                            ))}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </section>
              )}

              <section className="pgm-section">
                <h3 className="pgm-section-title">
                  Batches
                  <span className="pgm-section-count">{counts.total ?? 0}</span>
                </h3>

                <input
                  type="search"
                  className="pg-input pgm-search"
                  placeholder="Search by batch or course..."
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    runSearch(e.target.value);
                  }}
                  aria-label="Search batches"
                />

                {batches.length === 0 ? (
                  <p className="pgm-empty">
                    {query.trim()
                      ? <>No batch matches &ldquo;{query}&rdquo;.</>
                      : "No batches are assigned to this trainer."}
                  </p>
                ) : (
                  <table className="pgm-table pgm-students">
                    <thead>
                      <tr>
                        <th className="pgm-num">#</th>
                        <th>Batch</th>
                        <th>Runs</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {batches.map((b, i) => {
                        const status = BATCH_STATUS[b.status] || BATCH_STATUS.unscheduled;
                        return (
                          <tr
                            key={b.id || `${b.name}-${i}`}
                            className="pgm-row--open"
                            tabIndex={0}
                            role="button"
                            aria-label={`Open batch ${b.name || ""}`.trim()}
                            onClick={() => openBatch(b.id)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                openBatch(b.id);
                              }
                            }}
                          >
                            <td className="pgm-num">{i + 1}</td>
                            <td>
                              <span className="pgm-person-id">
                                <b className="pgm-person-name">{b.name || DASH}</b>
                                <span className="pgm-person-mail">
                                  {b.courseName || "No course"}
                                </span>
                              </span>
                            </td>
                            <td className="pgm-tel">
                              {formatDay(b.startTime)} – {formatDay(b.endTime)}
                            </td>
                            <td>
                              <span className={status.className}>{status.label}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}

                {matched > batches.length && (
                  <p className="pgm-empty">
                    Showing {batches.length} of {matched}. Search to narrow the list.
                  </p>
                )}
              </section>
            </>
          )}
        </div>

        {/* What you can do with the record sits with Close, at the end --
            you read the record, then act on it. Close stays last and is
            the only filled button, so the destructive one is never the
            default thing under the cursor. */}
        {!done && (
          <footer className="pg-modal-foot">
            <div className="pgm-actions">
              {canEdit && (
                <button type="button" className="pgm-btn" onClick={() => onEdit(trainer)}>
                  Edit
                </button>
              )}
              {canDelete && (
                <button
                  type="button"
                  className="pgm-btn pgm-btn--danger"
                  onClick={() => onDelete(trainer)}
                >
                  Delete
                </button>
              )}
            </div>
            <button type="button" className="pg-btn pg-btn--main" onClick={onClose}>
              Close
            </button>
          </footer>
        )}
      </div>
    </div>
  );
};

export default TrainerRecordModal;
